import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, ClipboardList } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const ACTION_LABELS = {
  "user.create": "Nov član",
  "user.update": "Sprememba člana",
  "user.delete": "Izbrisan član",
  "user.credentials_change": "Sprememba poverilnic",
  "finance.record_create": "Nov finančni zapis",
  "finance.record_delete": "Izbrisan finančni zapis",
  "finance.debt_create": "Nova finančna postavka",
  "finance.debt_delete": "Izbrisana finančna postavka",
};

const ACTION_ICONS = {
  "user.create": "👤+",
  "user.update": "✏️",
  "user.delete": "🗑",
  "user.credentials_change": "🔑",
  "finance.record_create": "💶+",
  "finance.record_delete": "💶−",
  "finance.debt_create": "🧾+",
  "finance.debt_delete": "🧾−",
};

const FILTERS = [
  { value: "", label: "Vse" },
  { value: "user.", label: "Člani" },
  { value: "finance.", label: "Finance" },
];

const DnevnikPage = () => {
  const { token } = useAuth();
  const [data, setData] = useState({ entries: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200", offset: "0" });
      if (action) params.set("action", action);
      const res = await axios.get(`${API}/dnevnik?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri branju dnevnika");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [action]); // eslint-disable-line

  const fmt = (iso) =>
    iso ? new Date(iso).toLocaleString("sl-SI", { dateStyle: "short", timeStyle: "short" }) : "";

  const renderDetails = (details) => {
    if (!details || Object.keys(details).length === 0) return null;
    const entries = [];
    if (details.znesek != null) entries.push(`${Number(details.znesek).toFixed(2)} €`);
    if (details.opis) entries.push(details.opis);
    if (details.tip) entries.push(`tip: ${details.tip}`);
    if (details.vloga) entries.push(`vloga: ${details.vloga}`);
    if (details.changed && Array.isArray(details.changed)) {
      entries.push(`spremenjeno: ${details.changed.join(", ")}`);
    }
    if (details.fields && Array.isArray(details.fields)) {
      entries.push(`polja: ${details.fields.join(", ")}`);
    }
    return entries.length > 0 ? entries.join(" · ") : null;
  };

  return (
    <div className="space-y-4" data-testid="dnevnik-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <ClipboardList className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary">Dnevnik dogodkov</h1>
            <p className="text-sm text-muted-foreground">
              Avtomatski pregled sprememb članov in finančnih dogodkov. Vidno samo vodstvu LD.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="dnevnik-refresh">
          <RefreshCw className="h-4 w-4 mr-2" /> Osveži
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((opt) => (
          <Button
            key={opt.value}
            size="sm"
            variant={action === opt.value ? "default" : "outline"}
            onClick={() => setAction(opt.value)}
            data-testid={`dnevnik-filter-${opt.value || "all"}`}
          >
            {opt.label}
          </Button>
        ))}
        <span className="text-xs text-muted-foreground ml-auto self-center">
          Prikazanih {data.entries.length} od {data.total}
        </span>
      </div>

      {loading ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Nalaganje…</CardContent></Card>
      ) : data.entries.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground text-sm">
            Ni zabeleženih dogodkov{action ? ` za filter "${FILTERS.find(f => f.value === action)?.label}"` : ""}.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.entries.map((e) => {
            const label = ACTION_LABELS[e.action] || e.action;
            const icon = ACTION_ICONS[e.action] || "•";
            const detailsText = renderDetails(e.details);
            const isFinance = (e.action || "").startsWith("finance.");
            return (
              <Card key={e.id} className={isFinance ? "border-l-4 border-l-amber-500 dark:border-l-amber-600" : "border-l-4 border-l-primary/40"} data-testid={`dnevnik-entry-${e.id}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-lg leading-none">{icon}</span>
                    <span className="font-semibold text-primary">{label}</span>
                    {e.target_name && (
                      <span className="text-sm text-muted-foreground">→ <b className="text-foreground">{e.target_name}</b></span>
                    )}
                    <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
                      {fmt(e.timestamp)}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                    {e.actor_name ? (
                      <span>Izvedel: <b>{e.actor_name}</b>{e.actor_role ? ` (${e.actor_role})` : ""}</span>
                    ) : (
                      <span className="italic">Sistem</span>
                    )}
                    {detailsText && (
                      <span className="text-[11px] font-mono bg-muted/50 dark:bg-muted/30 px-2 py-0.5 rounded">
                        {detailsText}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DnevnikPage;
